// LeadsWorkThisListButton — the "AI Work This List" surface.
//
// Big prominent button at the top of LeadsView. Click → dry-run
// against every visible lead → preview modal shows the AI's plan
// with counts, per-lead actions, sample messages. Operator can
// spot-check any card, then Approve to fire (or Cancel).
//
// Dry-run is the DEFAULT — nothing sends until Approve. Matches
// the pattern used for referral campaigns.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';
import { useLeadIntelligence } from './LeadIntelligenceContext';

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const ACTION_ICON = {
  call: '📞', sms: '💬', email: '✉️',
};

const PRIORITY_TONE = {
  call_now:   { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b' },
  call_today: { bg: '#fff7ed', border: '#fed7aa', text: '#9a3412' },
  follow_up:  { bg: '#fefce8', border: '#fde047', text: '#854d0e' },
  nurturing:  { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534' },
  at_risk:    { bg: '#faf5ff', border: '#e9d5ff', text: '#6b21a8' },
};

function PlanRow({ p }) {
  const t = PRIORITY_TONE[p.priority] || PRIORITY_TONE.nurturing;
  return (
    <div style={{
      background: '#ffffff', border: `1px solid ${t.border}`,
      borderLeft: `4px solid ${t.text}`, borderRadius: 8,
      padding: 10, fontSize: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 18 }}>{ACTION_ICON[p.action] || '·'}</span>
        <div style={{ fontWeight: 800, color: '#0f172a', flex: 1 }}>{p.name}</div>
        <span style={{
          background: t.bg, color: t.text, border: `1px solid ${t.border}`,
          padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.05em',
        }}>{p.priority.replace('_', ' ').toUpperCase()}</span>
      </div>
      <div style={{ color: '#334155', marginTop: 2 }}>
        <strong>{p.action.toUpperCase()}</strong> · {p.when} — {p.why}
      </div>
      {p.drafted_message && (
        <div style={{
          marginTop: 6, padding: '6px 8px', background: '#f8fafc',
          border: '1px solid #e2e8f0', borderRadius: 6,
          fontSize: 11, color: '#475569', fontStyle: 'italic',
        }}>"{p.drafted_message}"</div>
      )}
    </div>
  );
}

export default function LeadsWorkThisListButton({ leads = [], calls = [], outreach = [], showToast = () => {} }) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [plan, setPlan] = React.useState(null);
  const [error, setError] = React.useState(null);
  const intel = useLeadIntelligence();

  const active = React.useMemo(() =>
    (leads || []).filter(l => !['sold','closed','lost','dead','recycled','dnc'].includes(l.status || 'new'))
  , [leads]);

  const run = async () => {
    setOpen(true); setLoading(true); setError(null); setPlan(null);
    try {
      const callHistoryByLeadId = {};
      for (const c of calls) if (c.leadId) (callHistoryByLeadId[c.leadId] = callHistoryByLeadId[c.leadId] || []).push({ at: c.date, outcome: c.outcome });
      const outreachByLeadId = {};
      for (const o of outreach) if (o.leadId) (outreachByLeadId[o.leadId] = outreachByLeadId[o.leadId] || []).push({ at: o.createdAt, type: o.type });
      const r = await fetch('/api/leads/work-this-list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearer()) },
        body: JSON.stringify({
          leadIds: active.map(l => l.id),
          leads:   active.map(l => ({
            id: l.id, name: l.name, phone: l.phone, email: l.email,
            vertical: l.vertical, status: l.status, notes: l.notes,
            followUpDate: l.followUpDate,
            optOutSms: l.optOutSms, optOutEmail: l.optOutEmail,
          })),
          callHistoryByLeadId, outreachByLeadId,
          bestCallHour: intel.data?.predictions?.best_call_hour ?? null,
          commit: false,
        }),
      });
      const j = await r.json();
      if (r.ok && j.ok) setPlan(j);
      else setError(j.error || `HTTP ${r.status}`);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={run}
        disabled={active.length === 0}
        style={{
          background: active.length === 0
            ? '#e2e8f0'
            : 'linear-gradient(135deg, #0891b2 0%, #7c3aed 100%)',
          color: active.length === 0 ? '#94a3b8' : '#ffffff',
          border: 'none', borderRadius: 10, padding: '10px 18px',
          fontSize: 13, fontWeight: 800, cursor: active.length === 0 ? 'not-allowed' : 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 8,
          boxShadow: active.length === 0 ? 'none' : '0 4px 14px rgba(124,58,237,0.25)',
        }}
      >
        🤖 AI Work This List
        <span style={{
          background: 'rgba(255,255,255,0.2)', fontSize: 11, fontWeight: 700,
          padding: '2px 8px', borderRadius: 999,
        }}>{active.length}</span>
      </button>

      {open && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'grid', placeItems: 'center', zIndex: 1100, padding: 20,
        }} onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#ffffff', border: '1px solid #e2e8f0',
            borderRadius: 14, padding: 20, maxWidth: 720, width: '100%',
            maxHeight: '85vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#0891b2', fontWeight: 800 }}>
                  🤖 AI Work This List
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
                  Planned actions (dry-run)
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: 22, cursor: 'pointer' }}
              >×</button>
            </div>

            {loading && <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Planning…</div>}
            {error && <div style={{ padding: 16, color: '#dc2626', background: '#fef2f2', borderRadius: 8 }}>{error}</div>}

            {plan && (
              <>
                <div style={{
                  display: 'grid', gap: 8,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  marginBottom: 14,
                }}>
                  {[
                    ['Total input',    plan.summary.total_input],
                    ['Planned',        plan.summary.planned],
                    ['Skipped',        plan.summary.skipped],
                    ['Duplicates',     plan.summary.duplicates],
                    ['Calls',          plan.summary.calls_scheduled],
                    ['Texts',          plan.summary.texts_planned],
                  ].map(([label, val]) => (
                    <div key={label} style={{
                      background: '#f8fafc', border: '1px solid #e2e8f0',
                      borderRadius: 8, padding: '8px 10px', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', fontFamily: 'JetBrains Mono, monospace' }}>{val}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
                  {plan.plan.map(p => <PlanRow key={p.lead_id} p={p}/>)}
                </div>

                {plan.skipped.length > 0 && (
                  <div style={{
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: 8, padding: 10, fontSize: 11, color: '#475569',
                  }}>
                    <strong>{plan.skipped.length} skipped:</strong>{' '}
                    {plan.skipped.slice(0, 5).map(s => `${s.name || s.id}: ${s.reason}`).join(' · ')}
                    {plan.skipped.length > 5 && ` · +${plan.skipped.length - 5} more`}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button
                    onClick={() => setOpen(false)}
                    style={{
                      background: 'transparent', border: '1px solid #cbd5e1', color: '#475569',
                      padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}
                  >Cancel</button>
                  <button
                    onClick={() => {
                      showToast(`AI queued ${plan.summary.planned} action${plan.summary.planned === 1 ? '' : 's'}. (Live-fire wiring pending)`);
                      setOpen(false);
                    }}
                    style={{
                      background: '#059669', border: 'none', color: '#ffffff',
                      padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer',
                    }}
                  >Approve + Fire</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
